import {
	Children,
	cloneElement,
	isValidElement,
	MouseEvent,
	ReactElement,
	ReactNode,
} from 'react';
import {
	ButtonProps,
} from '@components/button';
//STYLES
import styles from './tab.module.scss';

export interface TabProps {
	'isActive'?: boolean;
	'children'?: ReactNode;
	'className'?: string;
	'data-testid'?: string;
	'disabled'?: boolean;
	'isSuccessful'?: boolean;
}

export const Tab = ({
	children,
	className,
	'data-testid': dataTestid,
	disabled,
	isActive = false,
	isSuccessful = false,
}: TabProps) => {
	const classes = [
		styles.tab,
	];

	if (isSuccessful) classes.push(styles.tab__successful);
	if (isActive) classes.push(styles.tab__active);
	if (disabled) classes.push(styles.tab__disabled);
	if (className) classes.push(className);

	const childClasses = [
		styles.child,
	];
	if (isSuccessful) childClasses.push(styles.child__successful);
	if (isActive) childClasses.push(styles.child__active);
	if (disabled) childClasses.push(styles.child__disabled);

	const applyClassesToChildren = (children: ReactNode): ReactNode => Children.map(children, (child, index) => {
		if (isValidElement(child)) {
			const childProps = child.props as TabProps;
			const existingClasses = childProps?.className ? childProps.className.split(' ') : [];
			const combinedClasses = [
				...existingClasses,
				...childClasses,
			].join(' ');

			const updatedChild = cloneElement(child as ReactElement, {
				'className': combinedClasses,
				'data-testid': `${dataTestid}-child-${index}`,
				'disabled': disabled,
				'onClick': (e: MouseEvent) => {
					if (disabled) {
						e.preventDefault();
						e.stopPropagation();
					}
					const buttonProps = child.props as ButtonProps;
					if (buttonProps?.onClick) {
						buttonProps.onClick(e as MouseEvent<HTMLElement>);
					}
				},
			} as TabProps);

			if ((child.props as TabProps).children) {
				return cloneElement(updatedChild, {
					children: applyClassesToChildren((child.props as TabProps).children),
					disabled: disabled,
				} as TabProps);
			}
			return updatedChild;
		}
		return child;
	});

	const updatedChildren = applyClassesToChildren(children);

	return (
		<div
			className={classes.join(' ')}
			data-testid={dataTestid}
		>
			{updatedChildren}
		</div>
	);
};
